import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaPlus, FaTrash, FaImages } from 'react-icons/fa';
import { API_BASE } from '../utils/helpers';

function BannerManager() {
  const [banners, setBanners] = useState([]);
  const [image, setImage] = useState(null);
  const [isValidSize, setIsValidSize] = useState(true);
  const [dimensions, setDimensions] = useState({ width: null, height: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { fetchBanners(); }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/banners`);
      setBanners(res.data || []);
    } catch (err) {
      setError('Failed to fetch banners');
    }
    setLoading(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setSuccess(''); setError(''); setDimensions({ width: null, height: null });
    setIsValidSize(true); setImage(file || null);
    if (!file) return;
    try {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const w = img.width, h = img.height;
        setDimensions({ width: w, height: h });
        if (w !== 600 || h !== 250) { setIsValidSize(false); setError('Image must be exactly 600 × 250 pixels'); }
        else setIsValidSize(true);
        URL.revokeObjectURL(objectUrl);
      };
      img.onerror = () => { setIsValidSize(false); setError('Unable to read image. Please try another file.'); URL.revokeObjectURL(objectUrl); };
      img.src = objectUrl;
    } catch (ex) {
      setIsValidSize(false); setError('Unable to process image.');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!image) { setError('Image is required'); return; }
    if (!isValidSize) { setError('Please upload an image of size 600 × 250 pixels'); return; }
    setLoading(true); setError(''); setSuccess('');
    const formData = new FormData();
    formData.append('image', image);
    try {
      await axios.post(`${API_BASE}/api/banners`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Banner created successfully');
      setImage(null); setDimensions({ width: null, height: null });
      fetchBanners();
    } catch (err) {
      setError('Failed to create banner');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      await axios.delete(`${API_BASE}/api/banners/${id}`);
      setSuccess('Banner deleted');
      fetchBanners();
    } catch (err) {
      setError('Failed to delete banner');
    }
    setLoading(false);
  };

  const fileInput =
    'block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100';

  const srcOf = (b) =>
    b.image_url?.startsWith('http') ? b.image_url : `${API_BASE}${b.image_url}`;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Banner Images</h2>
        <p className="text-sm text-slate-500">Home-screen banners shown in the mobile app.</p>
      </div>

      {success && <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">{success}</div>}
      {error && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">{error}</div>}

      {/* Upload */}
      <form onSubmit={handleCreate} className="ui-card mb-6 p-6">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">Upload banner</h3>
        <div className="max-w-xl">
          <input type="file" accept="image/*" onChange={handleImageChange} className={fileInput} required />
          <p className="mt-2 text-xs text-slate-500">Required size: <span className="font-medium text-slate-700">600 × 250 px</span></p>
          {dimensions.width && dimensions.height && (
            <p className={`mt-1 text-xs ${isValidSize ? 'text-slate-500' : 'text-rose-600'}`}>
              Selected: {dimensions.width} × {dimensions.height} px
            </p>
          )}
        </div>
        <div className="mt-4">
          <button type="submit" className="ui-btn-primary disabled:opacity-50" disabled={loading || !isValidSize}>
            <FaPlus className="text-xs" /> Create banner
          </button>
        </div>
      </form>

      {/* Grid */}
      {loading && banners.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="ui-card h-44 animate-pulse" />
          ))}
        </div>
      ) : banners.length === 0 ? (
        <div className="ui-card flex flex-col items-center justify-center gap-2 py-14 text-slate-400">
          <FaImages className="text-2xl" />
          <p className="text-sm">No banners yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {banners.map((banner) => (
            <div key={banner.id} className="ui-card overflow-hidden">
              <img
                src={srcOf(banner)}
                alt="Banner"
                className="h-40 w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="flex justify-end p-3">
                <button
                  className="ui-btn-ghost px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
                  onClick={() => handleDelete(banner.id)}
                  disabled={loading}
                >
                  <FaTrash className="text-xs" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BannerManager;
